import { useRef, useState, useEffect } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const fmtTime = (s: number) => {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

// Padrão fixo de barras (waveform estilizada, determinístico p/ visual estável).
const BARS = [
  0.32, 0.55, 0.42, 0.78, 0.5, 0.66, 0.38, 0.88, 0.54, 0.7, 0.34, 0.6, 0.82,
  0.46, 0.64, 0.5, 0.74, 0.4, 0.58, 0.86, 0.48, 0.68, 0.44, 0.6, 0.36, 0.8,
  0.52, 0.64, 0.42, 0.72, 0.5, 0.6,
];

/**
 * Player de áudio compacto e elegante, no tema do CRM — substitui o
 * `<audio controls>` nativo. Botão play em pílula + waveform clicável (seek) +
 * tempo. Toca dentro do app.
 */
export function AudioPlayer({ src, className, autoPlay, onError }: { src: string; className?: string; autoPlay?: boolean; onError?: () => void }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime);
    const onMeta = () => setDur(a.duration || 0);
    const onEnd = () => { setPlaying(false); setCur(0); };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('durationchange', onMeta);
    a.addEventListener('ended', onEnd);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('durationchange', onMeta);
      a.removeEventListener('ended', onEnd);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {}); else a.pause();
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const a = audioRef.current;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    a.currentTime = pct * dur;
    setCur(a.currentTime);
  };

  const pct = dur ? (cur / dur) * 100 : 0;

  return (
    <div
      className={cn(
        'inline-flex items-center gap-2.5 rounded-full border border-border/60 bg-gradient-to-r from-background to-muted/40 px-2 py-1.5 shadow-sm backdrop-blur-sm w-full',
        className,
      )}
    >
      <audio ref={audioRef} src={src} autoPlay={autoPlay} preload="metadata" className="hidden" onError={onError} />
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? 'Pausar' : 'Reproduzir'}
        className="grid place-items-center h-8 w-8 rounded-full bg-primary text-primary-foreground shadow-md hover:scale-105 active:scale-95 transition-transform shrink-0"
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
      </button>

      <div className="relative flex items-center gap-[2px] h-7 flex-1 min-w-[80px] cursor-pointer" onClick={seek}>
        {BARS.map((h, i) => {
          const barPct = ((i + 0.5) / BARS.length) * 100;
          const active = barPct <= pct;
          return (
            <span
              key={i}
              className={cn(
                'flex-1 rounded-full transition-all duration-200',
                active ? 'bg-primary' : 'bg-muted-foreground/25',
                playing && active && 'animate-pulse',
              )}
              style={{ height: `${Math.round(h * 100)}%` }}
            />
          );
        })}
      </div>

      <span className="text-[10px] tabular-nums text-muted-foreground shrink-0 pr-1 flex items-center gap-1">
        <Volume2 className="h-3 w-3 opacity-60" />
        {fmtTime(cur)} / {fmtTime(dur || 0)}
      </span>
    </div>
  );
}
